import React, { useState, useEffect } from 'react';
import { colors, radius, spacing, fontStack } from '../design-system/tokens';

/**
 * Sidebar — fixed left navigation, ~240px wide.
 * Phase 1: desktop-first; collapses to icon-only under 768px.
 *
 * Props:
 *   - currentPath: string (e.g. "/energy")
 *   - user: { email, user_metadata } | null
 *   - onSignOut: () => void
 *
 * Nav items (in order): Home, Energy, Growth, System, Copilot.
 */

const NAV_ITEMS = [
  { path: '/', label: 'Home', icon: HomeIcon },
  { path: '/energy', label: 'Energy', icon: BoltIcon, accent: colors.accentEnergy },
  { path: '/growth', label: 'Growth', icon: SeedlingIcon, accent: colors.accentGrowth },
  { path: '/system', label: 'System', icon: GridIcon, accent: colors.accentSystem },
  { path: '/copilot', label: 'Copilot', icon: ChatIcon },
];

const SIDEBAR_WIDTH = 240;
const SIDEBAR_WIDTH_COLLAPSED = 72;

export default function Sidebar({ currentPath = '/', user, onSignOut }) {
  const [collapsed, setCollapsed] = useState(
    typeof window !== 'undefined' && window.innerWidth < 768
  );

  useEffect(() => {
    const onResize = () => setCollapsed(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Prefer the Profile-page-edited `display_name`; fall back to `full_name`
  // (legacy from signup) and finally the email-prefix.
  const displayName =
    user?.user_metadata?.display_name ||
    user?.user_metadata?.full_name ||
    user?.email?.split('@')[0] ||
    'Account';
  const displayEmail = user?.email || '';
  const avatarUrl = user?.user_metadata?.avatar_url || null;

  const width = collapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH;

  return (
    <aside
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        bottom: 0,
        width,
        backgroundColor: colors.card,
        borderRight: `1px solid ${colors.border}`,
        display: 'flex',
        flexDirection: 'column',
        fontFamily: fontStack,
        zIndex: 50,
        transition: 'width 180ms ease',
      }}
    >
      {/* Brand */}
      <a
        href="/"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: collapsed ? '20px 16px' : '20px 20px',
          textDecoration: 'none',
          color: colors.textPrimary,
          borderBottom: `1px solid ${colors.border}`,
        }}
      >
        <CruxHexLogo />
        {!collapsed && (
          <span
            style={{
              fontSize: 17,
              fontWeight: 600,
              letterSpacing: '-0.01em',
              color: colors.textPrimary,
            }}
          >
            Curx AI
          </span>
        )}
      </a>

      {/* Nav */}
      <nav
        style={{
          flex: 1,
          padding: `${spacing.base}px ${collapsed ? 12 : 12}px`,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          overflowY: 'auto',
        }}
      >
        {NAV_ITEMS.map((item) => {
          const isActive = isPathActive(currentPath, item.path);
          const Icon = item.icon;
          return (
            <a
              key={item.path}
              href={item.path}
              title={item.label}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: collapsed ? '10px 0' : '10px 12px',
                justifyContent: collapsed ? 'center' : 'flex-start',
                borderRadius: radius.md,
                textDecoration: 'none',
                color: isActive ? colors.primary : colors.textPrimary,
                backgroundColor: isActive ? 'var(--primary-bg)' : 'transparent',
                fontSize: 14,
                fontWeight: isActive ? 600 : 500,
                transition: 'background-color 120ms ease, color 120ms ease',
              }}
              onMouseEnter={(e) => {
                if (!isActive) e.currentTarget.style.backgroundColor = 'var(--bg-hover)';
              }}
              onMouseLeave={(e) => {
                if (!isActive) e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <Icon color={isActive ? colors.primary : colors.textSecondary} />
              {!collapsed && <span>{item.label}</span>}
            </a>
          );
        })}
      </nav>

      {/* Footer: user + settings */}
      <div
        style={{
          borderTop: `1px solid ${colors.border}`,
          padding: collapsed ? '12px 8px' : '12px',
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
        }}
      >
        {user && !collapsed && (
          <a
            href="/profile"
            title="Open profile"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '8px 12px',
              borderRadius: radius.md,
              backgroundColor: 'var(--bg-surface-2)',
              textDecoration: 'none',
              color: 'inherit',
              transition: 'background-color 120ms ease',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--bg-hover)')}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'var(--bg-surface-2)')}
          >
            {avatarUrl ? (
              <img
                src={avatarUrl}
                alt=""
                width="28"
                height="28"
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  objectFit: 'cover',
                  flexShrink: 0,
                  border: `1px solid ${colors.border}`,
                }}
              />
            ) : (
              <div
                aria-hidden="true"
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  backgroundColor: colors.primary,
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 12,
                  fontWeight: 600,
                  flexShrink: 0,
                }}
              >
                {(displayName[0] || '?').toUpperCase()}
              </div>
            )}
            <div style={{ overflow: 'hidden', minWidth: 0, flex: 1 }}>
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: colors.textPrimary,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {displayName}
              </div>
              {displayEmail && (
                <div
                  style={{
                    fontSize: 11,
                    color: colors.textSecondary,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {displayEmail}
                </div>
              )}
            </div>
          </a>
        )}

        {/* Settings link */}
        <a
          href="/settings"
          title="Settings"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: collapsed ? '10px 0' : '10px 12px',
            justifyContent: collapsed ? 'center' : 'flex-start',
            borderRadius: radius.md,
            textDecoration: 'none',
            color: colors.textSecondary,
            fontSize: 13,
            fontWeight: 500,
          }}
          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--bg-hover)')}
          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
        >
          <CogIcon color={colors.textSecondary} />
          {!collapsed && <span>Settings</span>}
        </a>

        {user && onSignOut && !collapsed && (
          <button
            type="button"
            onClick={onSignOut}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '8px 12px',
              borderRadius: radius.md,
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              color: colors.textSecondary,
              fontSize: 12,
              fontFamily: fontStack,
              textAlign: 'left',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--bg-hover)')}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
          >
            Sign out
          </button>
        )}
      </div>
    </aside>
  );
}

/**
 * Sidebar layout helper — wraps page content with proper left margin.
 * Use as: <SidebarLayout currentPath={...} user={...} onSignOut={...}>...</SidebarLayout>
 */
export function SidebarLayout({ currentPath, user, onSignOut, children }) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' && window.innerWidth < 768
  );

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const sidebarWidth = isMobile ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH;
  // Tighter horizontal padding on mobile so cards fit comfortably at 375px:
  // 375 - 72 (sidebar) - 32 (16+16) = 271px content; previously 239px.
  const horizontalPad = isMobile ? spacing.base : spacing.xl;
  const verticalTopPad = isMobile ? spacing.lg : spacing.xl;

  return (
    <div
      style={{
        minHeight: '100vh',
        backgroundColor: colors.bg,
        fontFamily: fontStack,
        color: colors.textPrimary,
      }}
    >
      <Sidebar currentPath={currentPath} user={user} onSignOut={onSignOut} />
      <main
        style={{
          marginLeft: sidebarWidth,
          padding: `${verticalTopPad}px ${horizontalPad}px ${spacing.xxl}px`,
          minHeight: '100vh',
          transition: 'margin-left 180ms ease, padding 180ms ease',
        }}
      >
        {children}
      </main>
    </div>
  );
}

function isPathActive(currentPath, itemPath) {
  if (itemPath === '/') return currentPath === '/' || currentPath === '';
  return currentPath === itemPath || currentPath.startsWith(itemPath + '/');
}

/* === Inline icons (no external dep) === */

function CruxHexLogo() {
  return (
    <img
      src="/assets/crux-hex.png"
      alt="Curx AI"
      width="32"
      height="32"
      style={{ display: 'block' }}
    />
  );
}

function HomeIcon({ color }) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
         stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 11l9-8 9 8" />
      <path d="M5 10v10h14V10" />
    </svg>
  );
}

function BoltIcon({ color }) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
         stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polygon points="13 2 4 14 11 14 10 22 20 10 13 10 13 2" />
    </svg>
  );
}

function SeedlingIcon({ color }) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
         stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 21v-7" />
      <path d="M12 14c-4 0-7-3-7-7 4 0 7 3 7 7z" />
      <path d="M12 14c4 0 7-3 7-7-4 0-7 3-7 7z" />
    </svg>
  );
}

function GridIcon({ color }) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
         stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="3" width="7" height="7" />
      <rect x="14" y="3" width="7" height="7" />
      <rect x="3" y="14" width="7" height="7" />
      <rect x="14" y="14" width="7" height="7" />
    </svg>
  );
}

function ChatIcon({ color }) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
         stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 11.5a8.4 8.4 0 0 1-8.4 8.4 8.6 8.6 0 0 1-3.7-.8L3 21l1.9-5.7a8.4 8.4 0 0 1 16.1-3.8z" />
    </svg>
  );
}

function CogIcon({ color }) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
         stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1A2 2 0 1 1 4.3 17l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1A1.7 1.7 0 0 0 4.6 9 1.7 1.7 0 0 0 4.3 7.2l-.1-.1A2 2 0 1 1 7 4.3l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" />
    </svg>
  );
}
